import type { AlgoliaHit } from '../types';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import algoliasearch from 'algoliasearch';
import { APP_CONSTANTS } from '~/common/explicit-exports';
import SearchApi from '~/agent/search';

const DEFAULT_COMMUNITY_ATTRIBUTES_TO_SNIPPET = ['title:20', 'content:20', 'description:20'];
const DEFAULT_ACADEMY_ATTRIBUTES_TO_SNIPPET = ['name', 'title', 'description', 'content', 'section_and_task_titles'];
const COMMUNITIES_INDEX = `${APP_CONSTANTS.ALGOLIA_INDEXES.COMMUNITIES}_${window.env}`;
const ACADEMIES_INDEX = `${APP_CONSTANTS.ALGOLIA_INDEXES.ACADEMIES}_${window.env}`;

interface AlgoliaSearchInterface {
  algoliaAppId: string;
  algoliaSearchApiKey: string;
  value: string;
  params?: { filters?: string; numericFilters?: string[] };
  academyAttributesToSnippet?: string[];
  communityAttributesToSnippet?: string[];
}

const searchAlgolia = async ({
  algoliaAppId,
  algoliaSearchApiKey,
  value,
  params,
  academyAttributesToSnippet,
  communityAttributesToSnippet,
}: AlgoliaSearchInterface): Promise<AlgoliaHit[]> => {
  const client = algoliasearch(algoliaAppId, algoliaSearchApiKey);
  const queries = [
    {
      indexName: COMMUNITIES_INDEX,
      attributesToSnippet: communityAttributesToSnippet || DEFAULT_COMMUNITY_ATTRIBUTES_TO_SNIPPET,
      query: value,
      hitsPerPage: 10,
      params,
    },
  ];
  queries.push({
    indexName: ACADEMIES_INDEX,
    attributesToSnippet: academyAttributesToSnippet || DEFAULT_ACADEMY_ATTRIBUTES_TO_SNIPPET,
    query: value,
    hitsPerPage: 15, // add a few more to account for dupes from custom pages
    params,
  });

  const { results } = await client.multipleQueries<AlgoliaHit>(queries);
  const academyResults = results.find((r) => ('index' in r ? r.index : '') === ACADEMIES_INDEX);
  SearchApi.logSearchResults(academyResults);
  const hits = results.map((r) => ('hits' in r ? r.hits : [])).flat();

  const hitsHash = hits.reduce((acc, hit) => {
    // Dedupe by parent id or use random uuid so as not to dedupe cases that do not have parents
    // Do this so that content that is in multiple spots on a custom page is not shown twice
    const parentId = 'parent_id' in hit ? hit.parent_id : uuidv4();
    const contentId = 'content_id' in hit ? hit.content_id : uuidv4();
    acc[`${parentId}${contentId}`] = hit;
    return acc;
  }, {});

  return Object.values(hitsHash);
};

export const useAlgoliaHubSearch = ({
  algoliaAppId,
  algoliaSearchApiKey,
  value,
  params,
  academyAttributesToSnippet,
  communityAttributesToSnippet,
}: AlgoliaSearchInterface) =>
  useQuery<AlgoliaHit[], JQuery.jqXHR>(
    ['ALGOLIA_SEARCH', value, params?.filters, ...(params?.numericFilters || [])],
    () =>
      searchAlgolia({
        algoliaAppId,
        algoliaSearchApiKey,
        value,
        params,
        academyAttributesToSnippet,
        communityAttributesToSnippet,
      }),
    {
      cacheTime: 0,
      enabled: !!value, // Only run the query if `value` is not empty
    },
  );
